import { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import {
  BrandGlyph,
  BRAND_VERSION,
  MODULE_COLOR,
  MODULE_TITLE,
} from '../lib/branding';
import { AuthBadge } from '../components/AuthBadge';
import { safeTopPad, safeBottomPad } from '../lib/safeArea';

export default function LandingPage() {
  return (
    <main style={mainStyle}>
      <Atmosphere />

      <div style={contentStyle}>
        <Nav />
        <Hero />
        <Modules />
        <Why />
        <ForWhom />
        <Disclaimer />
        <Footer />
      </div>
    </main>
  );
}

/* ════════════════ Sections ════════════════ */

function Nav() {
  return (
    <nav style={navStyle}>
      <div style={navBrandStyle}>
        <BrandGlyph size={28} color={MODULE_COLOR.angle} />
        <span style={navWordmarkStyle}>CADtomie Intraop</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <AuthBadge />
        <Link to="/app" style={navCtaStyle}>
          Open app →
        </Link>
      </div>
    </nav>
  );
}

function Hero() {
  // In-page smooth scroll — no route change, no URL hash, no page reload.
  // Works reliably on iPhone Safari and respects the body's
  // scroll-behavior: smooth fallback.
  const scrollToModules = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('modules');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section style={heroStyle}>
      <div style={heroEyebrowStyle}>For orthopaedic research and education</div>
      <h1 style={heroTitleStyle}>
        The OR ruler.
        <br />
        <span style={heroAccentSpanStyle}>Reimagined.</span>
      </h1>
      <p style={heroSubtitleStyle}>
        Three camera- and sensor-assisted reference tools for angle, distance,
        and torsion checks on iPhone.
      </p>
      <div style={heroCtaRowStyle}>
        <Link to="/app" style={primaryCtaStyle}>
          Open app
        </Link>
        <button type="button" onClick={scrollToModules} style={secondaryCtaStyle}>
          See the modules
        </button>
      </div>
      <div style={heroNoteStyle}>
        Works on iPhone Safari · Add to Home Screen for fullscreen · Local
        processing · No image upload
      </div>
    </section>
  );
}

function Modules() {
  const items: {
    key: 'angle' | 'height' | 'torsion';
    head: string;
    body: string;
  }[] = [
    {
      key: 'angle',
      head: 'Two-line goniometer on a fluoroscopy frame.',
      body:
        'Capture a reference image with the camera, drag two lines onto the visible references, and document the angle. Free-line mode supports two independent references; shared-vertex mode supports opening-wedge geometry. Results are intended for research assistance and must be verified independently.',
    },
    {
      key: 'height',
      head: 'Distance with optional mm calibration.',
      body:
        'Two-point distance check on a captured frame. Optional calibration against a coplanar reference of known length converts pixels to millimetres. Results depend on image plane, calibration, and parallax, and must be verified independently.',
    },
    {
      key: 'torsion',
      head: 'Live digital goniometer for rotational correction.',
      body:
        'Use the phone as a gravity-referenced goniometer. Set zero against a reference position, capture start and end, and export a side-by-side documentation report. Not for standalone navigation or treatment decisions.',
    },
  ];

  return (
    <section id="modules" style={sectionStyle}>
      <SectionLabel>Three modules</SectionLabel>

      <div style={modulesContainerStyle}>
        {items.map((m) => (
          <Link
            key={m.key}
            to={`/app/${m.key}`}
            style={moduleRowStyle}
            aria-label={`Open ${MODULE_TITLE[m.key]} module`}
          >
            <div style={moduleAccentStyle(MODULE_COLOR[m.key])}>
              <BrandGlyph size={18} color={MODULE_COLOR[m.key]} />
              <span style={moduleAccentTextStyle(MODULE_COLOR[m.key])}>
                {MODULE_TITLE[m.key]}
              </span>
            </div>
            <h3 style={moduleHeadStyle}>{m.head}</h3>
            <p style={moduleBodyStyle}>{m.body}</p>
            <span style={moduleOpenStyle(MODULE_COLOR[m.key])}>
              Open module&nbsp;<span aria-hidden>›</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Why() {
  const points: { title: string; body: string }[] = [
    {
      title: 'Local processing',
      body:
        'Camera frames and sensor data never leave the device. No backend, no upload, no PHI risk.',
    },
    {
      title: 'Gravity-referenced',
      body:
        'The torsion goniometer uses the accelerometer-derived gravity vector, not the magnetometer — unaffected by ferromagnetic instruments and the C-arm.',
    },
    {
      title: 'Local-first workflow',
      body:
        'The web version runs as a Progressive Web App. Open in Safari, optionally add to Home Screen for fullscreen. The native app uses the same local processing model.',
    },
    {
      title: 'Built by a surgeon',
      body:
        'Designed around real intraoperative ergonomics — large touch targets, one-handed operation, OR-friendly dark theme.',
    },
  ];
  return (
    <section style={sectionStyle}>
      <SectionLabel>Why this exists</SectionLabel>
      <div style={whyGridStyle}>
        {points.map((p) => (
          <div key={p.title} style={whyItemStyle}>
            <div style={whyTitleStyle}>{p.title}</div>
            <div style={whyBodyStyle}>{p.body}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ForWhom() {
  return (
    <section style={sectionStyle}>
      <SectionLabel>For whom</SectionLabel>
      <p style={prosePaintStyle}>
        Orthopaedic trauma, foot &amp; ankle, deformity correction, paediatric
        orthopaedics, research, and training workflows where an angle, distance,
        or torsion reference may be useful. CADtomie Intraop is not a substitute
        for fluoroscopy, 3D navigation, or clinical judgement.
      </p>
    </section>
  );
}

function Disclaimer() {
  return (
    <section style={disclaimerSectionStyle}>
      <div style={disclaimerCardStyle}>
        <div style={disclaimerLabelStyle}>Important</div>
        <p style={disclaimerBodyStyle}>
          CADtomie Intraop is shared as a research and educational assistance
          tool. It is <b>not a medical device</b>, has not received CE
          certification, and is not intended to diagnose, treat, monitor, or
          replace established imaging, navigation, or clinical judgement.
          Always verify results with an established method.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={footerStyle}>
      <div style={footerColStyle}>
        <div style={footerLabelStyle}>Product</div>
        <Link to="/app" style={footerLinkStyle}>Open app</Link>
        <a href="#modules" style={footerLinkStyle}>Modules</a>
      </div>
      <div style={footerColStyle}>
        <div style={footerLabelStyle}>Legal</div>
        <Link to="/privacy" style={footerLinkStyle}>Privacy</Link>
        <Link to="/terms" style={footerLinkStyle}>Terms</Link>
        <Link to="/impressum" style={footerLinkStyle}>Impressum</Link>
      </div>
      <div style={footerColStyle}>
        <div style={footerLabelStyle}>Contact</div>
        <a href="mailto:cadtomie@gmail.com" style={footerLinkStyle}>
          cadtomie@gmail.com
        </a>
      </div>
      <div style={{ ...footerColStyle, marginLeft: 'auto', textAlign: 'right' }}>
        <div style={footerLabelStyle}>Version</div>
        <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>v{BRAND_VERSION}</div>
      </div>
    </footer>
  );
}

/* ════════════════ Atomic ════════════════ */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={sectionLabelStyle}>
      <span style={sectionLabelLineStyle} />
      {children}
    </div>
  );
}

function Atmosphere() {
  return <div style={atmosphereStyle} aria-hidden />;
}

/* ════════════════ Styles ════════════════ */

const mainStyle: CSSProperties = {
  // The landing page must scroll naturally on iPhone Safari — no height locks,
  // no overflow:hidden. The fixed-position background handles the cinematic
  // wash without affecting layout flow.
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  background: '#000',
};

const atmosphereStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: `
    radial-gradient(circle at 20% -5%, rgba(124,196,255,0.14), transparent 45%),
    radial-gradient(circle at 95% 30%, rgba(255,159,10,0.08), transparent 50%),
    radial-gradient(circle at 50% 110%, rgba(48,209,88,0.05), transparent 50%),
    linear-gradient(180deg, #050507 0%, #000 60%, #000 100%)
  `,
  pointerEvents: 'none',
  zIndex: 0,
};

const contentStyle: CSSProperties = {
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  maxWidth: 980,
  width: '100%',
  margin: '0 auto',
  padding: '0 22px',
};

const navStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  // Inline safe-area math — keeps the nav below the Dynamic Island
  // in PWA standalone mode. Never overridden by other inline padding.
  paddingTop: safeTopPad(18),
  paddingBottom: 18,
};
const navBrandStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};
const navWordmarkStyle: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  letterSpacing: -0.1,
  color: 'var(--text)',
};
const navCtaStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text)',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  padding: '8px 14px',
  borderRadius: 999,
  textDecoration: 'none',
};

const heroStyle: CSSProperties = {
  paddingTop: 48,
  paddingBottom: 56,
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  alignItems: 'flex-start',
};
const heroEyebrowStyle: CSSProperties = {
  fontSize: 12,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  color: 'var(--text-dim)',
  fontWeight: 600,
};
const heroTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(48px, 8vw, 78px)',
  fontWeight: 700,
  letterSpacing: -2,
  lineHeight: 1.02,
  background:
    'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.78) 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
};
const heroAccentSpanStyle: CSSProperties = {
  background: `linear-gradient(180deg, ${MODULE_COLOR.angle} 0%, #4a9fdc 100%)`,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
};
const heroSubtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(16px, 2.4vw, 19px)',
  lineHeight: 1.5,
  color: 'var(--text-dim)',
  maxWidth: 540,
};
const heroCtaRowStyle: CSSProperties = {
  display: 'flex',
  gap: 12,
  flexWrap: 'wrap',
  marginTop: 12,
};
const primaryCtaStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 52,
  padding: '0 24px',
  borderRadius: 999,
  background: '#ffffff',
  color: '#000',
  fontWeight: 600,
  fontSize: 16,
  textDecoration: 'none',
  letterSpacing: -0.1,
};
const secondaryCtaStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 52,
  padding: '0 22px',
  borderRadius: 999,
  background: 'rgba(255,255,255,0.06)',
  color: 'var(--text)',
  border: '1px solid rgba(255,255,255,0.12)',
  fontWeight: 600,
  fontSize: 16,
  textDecoration: 'none',
  // button-as-link: kill the default browser styling
  cursor: 'pointer',
  fontFamily: 'inherit',
  letterSpacing: -0.1,
};
const heroNoteStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--text-muted)',
  letterSpacing: 0.1,
  marginTop: 4,
};

const sectionStyle: CSSProperties = {
  paddingTop: 48,
  paddingBottom: 48,
  display: 'flex',
  flexDirection: 'column',
  gap: 28,
};
const sectionLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 12,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  color: 'var(--text-dim)',
  fontWeight: 600,
};
const sectionLabelLineStyle: CSSProperties = {
  display: 'inline-block',
  width: 24,
  height: 1,
  background: 'rgba(255,255,255,0.4)',
};

const modulesContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 32,
};
const moduleRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '24px 0',
  borderTop: '1px solid rgba(255,255,255,0.08)',
  textDecoration: 'none',
  color: 'inherit',
  // Slight tactile feedback on tap (iOS adds its own halo too).
  WebkitTapHighlightColor: 'transparent',
};
function moduleOpenStyle(accent: string): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: 4,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.2,
    color: accent,
    textTransform: 'uppercase',
  };
}
function moduleAccentStyle(accent: string): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    alignSelf: 'flex-start',
    padding: '4px 12px 4px 8px',
    borderRadius: 999,
    background: `${accent}14`,
    border: `1px solid ${accent}3d`,
  };
}
function moduleAccentTextStyle(accent: string): CSSProperties {
  return {
    fontSize: 12,
    fontWeight: 700,
    color: accent,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  };
}
const moduleHeadStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(22px, 3.6vw, 28px)',
  fontWeight: 600,
  letterSpacing: -0.5,
  color: 'var(--text)',
  lineHeight: 1.2,
  maxWidth: 720,
};
const moduleBodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 16,
  lineHeight: 1.55,
  color: 'var(--text-dim)',
  maxWidth: 680,
};

const whyGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 18,
};
const whyItemStyle: CSSProperties = {
  padding: 20,
  borderRadius: 18,
  background: 'rgba(20,20,24,0.7)',
  border: '1px solid rgba(255,255,255,0.07)',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const whyTitleStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--text)',
};
const whyBodyStyle: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--text-dim)',
};

const prosePaintStyle: CSSProperties = {
  margin: 0,
  fontSize: 17,
  lineHeight: 1.6,
  color: 'var(--text-dim)',
  maxWidth: 720,
};

const disclaimerSectionStyle: CSSProperties = {
  paddingTop: 16,
  paddingBottom: 48,
};
const disclaimerCardStyle: CSSProperties = {
  padding: 22,
  borderRadius: 18,
  background: 'rgba(255,159,10,0.06)',
  border: '1px solid rgba(255,159,10,0.28)',
};
const disclaimerLabelStyle: CSSProperties = {
  fontSize: 12,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  fontWeight: 700,
  color: '#ff9f0a',
  marginBottom: 8,
};
const disclaimerBodyStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--text-dim)',
};

const footerStyle: CSSProperties = {
  marginTop: 'auto',
  paddingTop: 32,
  // Safe-area-aware bottom padding (home indicator on iPhone).
  paddingBottom: safeBottomPad(24),
  borderTop: '1px solid rgba(255,255,255,0.08)',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 32,
};
const footerColStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const footerLabelStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  fontWeight: 600,
  marginBottom: 4,
};
const footerLinkStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--text-dim)',
  textDecoration: 'none',
};
